import { createContext } from 'react';
import { Platform } from 'react-native';
import { create } from 'zustand';
import {
  persist,
  createJSONStorage,
  subscribeWithSelector
} from 'zustand/middleware';
import { encode } from 'base-64';
import _ from 'lodash';

import { getMicrophones, getSpeakers } from '../Sound';
import { init as initElectron } from './Electron';
import { init as initEvents } from './Events';
import Contacts from './Contacts';
import LorentzService from '../services/LorentzService';
import {
  authenticateAgent,
  LorentzSession,
  parseJwtExpiry
} from '../api/LorentzAuth';
import { applyTheme, initThemeWatcher, resolveThemeId } from '../theme';
import { loadViewLayout, mergeViewLayout } from '../utils/viewLayout';

const contacts = new Contacts();

const COOKIE_ID = 'KZS';
const CONTACTS_ID = 'KZSC';

const initializeStore = async state => {
  const requestPermissions = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true
      });
    } catch (err) {
      console.error('Failed obtaining audio permissions', err);
    }

    try {
      await Notification.requestPermission();
    } catch (err) {
      console.error('Failed obtaining Notification permissions', err);
    }
  };

  const initAudioDevices = async () => {
    const setAudioDevices = async () => {
      try {
        const devices = await getSpeakers();
        const microphones = await getMicrophones();

        state.setSettings({ devices, microphones });
      } catch (err) {
        console.error(err);
      }
    };

    if (Platform.OS === 'web')
      navigator.mediaDevices.ondevicechange = setAudioDevices;
    setAudioDevices();
  };

  const initContacts = () => {
    if (Platform.OS === 'web') {
      document.addEventListener('indexing:end', async () => {
        const contactsDialer = contacts.query({ query: '' });
        state.setSettings({ contactsDialer });

        contacts.save(CONTACTS_ID);
      });
    }

    contacts.load(CONTACTS_ID);
  };

  await requestPermissions();
  initAudioDevices();

  initElectron();
  initEvents();

  const { lorentz = {}, theme = 'auto' } = state;
  applyTheme(theme);
  initThemeWatcher(() => {
    const s = useStore.getState();
    if (s.theme === 'auto') {
      const result = applyTheme('auto');
      useStore.getState().setSettings({ themeResolved: result.resolvedId });
    }
  });
  initExtensionTimerListeners();
  touchExtensionTimers({ statusSince: Date.now() });

  const layoutId =
    lorentz.viewLayoutId ||
    state.viewLayoutId ||
    (lorentz.supervisorEmbed ? 'supervisor-embed' : 'operator-default');
  state.applyViewLayout?.(layoutId).catch(() => {});

  if (lorentz.enabled && lorentz.authenticated && lorentz.token) {
    LorentzService.init();
  }

  initContacts();
};

const touchExtensionTimers = (patch = {}) => {
  const now = Date.now();
  const timers = useStore.getState().extensionTimers || {};
  useStore.getState().setSettings({
    extensionTimers: { ...timers, ...patch, updatedAt: now }
  });
};

const initExtensionTimerListeners = () => {
  if (Platform.OS !== 'web') return;

  const onStatus = () => {
    const now = Date.now();
    const { status } = useStore.getState();
    touchExtensionTimers({
      statusSince: now,
      pauseSince: status === 'online' ? null : now
    });
  };

  const onCallStart = () => touchExtensionTimers({ callSince: Date.now() });
  const onCallEnd = () => touchExtensionTimers({ callSince: null });

  const onCtiLogin = () =>
    touchExtensionTimers({ sessionSince: Date.now() });
  const onCtiLogout = () =>
    touchExtensionTimers({ sessionSince: null, callSince: null });

  document.addEventListener('status:change', onStatus);
  document.addEventListener('phone:ringing', onCallStart);
  document.addEventListener('phone:accepted', onCallStart);
  document.addEventListener('phone:terminated', onCallEnd);
  document.addEventListener('lorentz:cti:login', onCtiLogin);
  document.addEventListener('lorentz:cti:logout', onCtiLogout);
};

const DEFAULTS = {
  statuses: [
    { value: 'online', text: 'Online', color: '#057e74' },
    { value: 'offline', text: 'Offline', color: '#A9A9A9' }
  ],

  numbers: [],

  sipUri: undefined,
  sipUser: undefined,
  sipPassword: undefined,
  wsUri: undefined,

  allowTransfer: true,
  allowBlindTransfer: true,
  allowVideo: true,
  allowAutoanswer: false,
  autoanswer: 5,

  settingsUri: undefined,
  nickname: undefined,
  password: undefined,
  avatar: undefined,

  webhooks: [],

  qTts: true,
  qSummarization: false,
  events: [],

  size: { width: 380, height: 520 },

  server: false,

  theme: 'auto',
  themeResolved: 'lorentz-light',
  viewLayout: null,

  extensionTimers: {
    statusSince: null,
    sessionSince: null,
    callSince: null,
    pauseSince: null
  },

  /** Integração LorentzServer — ver docs/integracao-lorentz.md */
  lorentz: {
    enabled: false,
    apiBaseUrl: undefined,
    token: undefined,
    tokenExpiresAt: undefined,
    username: undefined,
    groupName: undefined,
    authenticated: false,
    agentId: undefined,
    groupId: undefined,
    extension: undefined,
    screenPopUrl: undefined,
    syncContactOnRinging: true,
    qualifyOnHangup: true,
    pollIntervalMs: 2000,
    qualificationPauseReasonId: undefined,
    ctiLogin: true,
    powerDialing: true,
    autoDialPowerDialing: true,
    ctiAnswerOnRing: false,
    ctiSyncHold: true,
    recordingEnabled: true,
    predictiveEnabled: true,
    predictiveUraExtension: undefined,
    supervisorEmbed: false,
    supervisorPanels: {},
    screenPopParams: ['phone', 'call_id', 'cpf', 'campaign_id'],
    statusMap: {}
  },

  lorentzPauseReasons: []
};

const DEFAULTS_NOCONFIG = {
  status: 'online',
  number_out: undefined,

  devices: [],
  microphones: [],
  ringer: 'default',
  speaker: 'default',
  microphone: 'default',
  contactsDialer: {},
  contactsDialerFilter: '',
  cdrs: []
};

export const useStore = create(
  persist(
    subscribeWithSelector((set, get) => {
      return {
        ...DEFAULTS,
        ...DEFAULTS_NOCONFIG,

        electron: false,
        anchored: true,
        doquit: false,

        setSettings: settings => {
          set(() => ({ ...settings }));
        },

        setTheme: theme => {
          const result = applyTheme(theme);
          set(() => ({
            theme,
            themeResolved: result.resolvedId
          }));
        },

        applyViewLayout: async (layoutId, overrides = {}) => {
          const layout = await loadViewLayout(layoutId);
          if (!layout) return null;
          const merged = mergeViewLayout(layout, overrides);
          const patch = { viewLayout: merged };

          if (merged.theme) {
            applyTheme(merged.theme);
            patch.theme = merged.theme;
            patch.themeResolved = resolveThemeId(merged.theme);
          }
          if (merged.size) patch.size = merged.size;
          if (merged.lorentz) {
            patch.lorentz = {
              ...get().lorentz,
              ...merged.lorentz
            };
          }

          set(() => patch);
          return merged;
        },

        login: async ({
          settingsUri,
          nickname,
          password,
          lorentzCredentials
        }) => {
          if (!settingsUri) throw new Error(`No settings uri available!`);

          const headers = {};
          if (nickname || password) {
            headers.Authorization = `Basic ${encode(`${nickname || ''}:${password || ''}`)}`;
          }

          let response;

          if (!/^(https?|file):\/\//i.test(settingsUri)) {
            try {
              response = await fetch(`file://${settingsUri}`, {
                method: 'GET',
                headers
              });
            } catch (err) {
              console.error(err);
            }
          }

          if (!response?.ok) {
            response = await fetch(settingsUri, {
              method: 'GET',
              headers,
              credentials: 'include'
            });

            if (!response.ok)
              throw new Error(`HTTP status: ${response.status}`);
          }

          const settings = await response.json();
          const { number_out, numbers = [] } = settings;
          if (!number_out) {
            settings.number_out = numbers[0]?.name || '';
          }

          let lorentzMerged = {
            ...DEFAULTS.lorentz,
            ...(settings.lorentz || {})
          };

          if (lorentzCredentials && lorentzMerged.enabled && lorentzMerged.apiBaseUrl) {
            const auth = await authenticateAgent({
              apiBaseUrl: lorentzMerged.apiBaseUrl,
              username: lorentzCredentials.username,
              password: lorentzCredentials.password,
              group: lorentzCredentials.groupName
            });

            LorentzSession.setPassword(lorentzCredentials.password);

            const agent = auth.agent || {};
            lorentzMerged = {
              ...lorentzMerged,
              token: auth.token,
              tokenExpiresAt: auth.expiresAt || parseJwtExpiry(auth.token),
              username: lorentzCredentials.username,
              groupName: lorentzCredentials.groupName,
              authenticated: true,
              agentId: agent.id ?? agent.agent_id ?? lorentzMerged.agentId,
              groupId: agent.group_id ?? lorentzMerged.groupId,
              extension:
                agent.extension ?? lorentzMerged.extension ?? settings.sipUser
            };
          } else if (lorentzMerged.enabled && lorentzMerged.token) {
            lorentzMerged.authenticated = true;
          } else if (lorentzMerged.supervisorEmbed && settings.sipUri) {
            lorentzMerged.authenticated = true;
          }

          set(() => ({
            ...DEFAULTS,
            ...settings,
            lorentz: lorentzMerged,
            settingsUri,
            showSettings: false
          }));

          const layoutId =
            lorentzMerged.viewLayoutId ||
            settings.viewLayoutId ||
            (lorentzMerged.supervisorEmbed ? 'supervisor-embed' : 'operator-default');
          await get().applyViewLayout(layoutId).catch(() => {});

          if (settings.theme) {
            get().setTheme(settings.theme);
          }

          LorentzService.reset();
          LorentzService.init();
          touchExtensionTimers({ sessionSince: Date.now() });
        },

        lorentzLogout: async () => {
          await LorentzService.logout();
          LorentzSession.clear();
          set(state => ({
            lorentz: {
              ...state.lorentz,
              token: undefined,
              tokenExpiresAt: undefined,
              authenticated: false
            }
          }));
        },

        showSettings: false,
        settingsTab: null,
        toggleShowSettings: (value, tab) => {
          set(state => ({
            showSettings: _.isNil(value) ? !state.showSettings : value,
            settingsTab: tab
          }));
        },

        contacts: () => contacts,
        contactsDialer: {},
        contactsDialerFilter: '',
        setContactsDialerFilter: contactsDialerFilter => {
          const contactsDialer = contacts.query({
            query: contactsDialerFilter
          });
          set(() => ({ contactsDialer, contactsDialerFilter }));
        },

        cdrAdd: cdr => {
          const { cdrs } = get();
          const { headers, ...rest } = cdr;
          cdrs.unshift(rest);
          set(() => ({ cdrs: cdrs.slice(0, 100) }));
        },

        eventAdd: event => {
          const { events } = get();
          events.unshift(event);
          set(() => ({ events: events.slice(0, 100) }));
        },

        clear: () => {
          localStorage.clear();
          contacts.clear();

          set(() => ({ ...DEFAULTS, ...DEFAULTS_NOCONFIG }));

          initializeStore(get());
        }
      };
    }),
    {
      name: COOKIE_ID,
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state, err) => {
        if (!err) initializeStore(useStore.getState());
      },
      partialize: ({
        doquit,
        devices,
        showSettings,
        settingsTab,
        showWebhookForm,
        ...rest
      }) => ({ ...rest })
    }
  )
);

export const Context = createContext();

export const ContextProvider = ({ children }) => (
  <Context.Provider value={useStore()}>{children}</Context.Provider>
);
